import { isDefined } from '@pflegenavi/shared/utils';
import type { LineItem } from '../ServiceProviderReceiptBatchForm';
import isEqual from 'lodash.isequal';

export type ServerBatch = UpdateBatchEntries[];

export interface UpdateBatchEntries {
  id?: string;
  receiptImageIds?: string[] | null;
  lineItems: LineItem[] | undefined;
}

interface UseUpdateReceiptBatchWithMergeBaseParams {
  oldServerBatch: UpdateBatchEntries[];
  newServerBatch: UpdateBatchEntries[];
  localEntries: UpdateBatchEntries[];
}

/**
 * This function updates `entries` and add `receiptImageIds` if they were added on the server. Or removes them
 * if they were removed locally.
 */
export const updateReceiptBatchWithMergeBase = (
  opts: UseUpdateReceiptBatchWithMergeBaseParams
): UpdateBatchEntries[] => {
  const { oldServerBatch, newServerBatch, localEntries } = opts;

  // eslint-disable-next-line complexity
  const localUpdated = localEntries.map((localEntry) => {
    const oldServerEntry = oldServerBatch.find(
      (entry) => entry.id === localEntry.id
    );
    const newServerEntry = newServerBatch.find(
      (entry) => entry.id === localEntry.id
    );

    if (!oldServerEntry || !newServerEntry) {
      return {
        ...localEntry,
        receiptImageIds: localEntry.receiptImageIds ?? [],
      };
    }

    const receiptImageIds = mergeImages(
      oldServerEntry,
      newServerEntry,
      localEntry
    );

    const lineItems = mergeLineItems(
      oldServerEntry.lineItems || [],
      newServerEntry.lineItems || [],
      localEntry.lineItems || []
    );

    return {
      ...localEntry,
      lineItems,
      receiptImageIds,
    };
  });

  const deletedEntries = oldServerBatch.filter((oldServerEntry) => {
    return !localUpdated.find(
      (newServerEntry) => newServerEntry.id === oldServerEntry.id
    );
  });

  const localAdded = localUpdated.filter((localEntry) => {
    return (
      !oldServerBatch.find(
        (oldServerEntry) => oldServerEntry.id === localEntry.id
      ) &&
      !newServerBatch.find(
        (newServerEntry) => newServerEntry.id === localEntry.id
      )
    );
  });

  return newServerBatch
    .map((newServerEntry) => {
      const localEntry = localUpdated.find(
        (entry) => entry.id === newServerEntry.id
      );
      if (localEntry) {
        return localEntry;
      }
      if (deletedEntries.find((entry) => entry.id === newServerEntry.id)) {
        return undefined;
      }
      return newServerEntry;
    })
    .concat(localAdded)
    .filter(isDefined);
};

function mergeImages(
  oldServerEntry: UpdateBatchEntries,
  newServerEntry: UpdateBatchEntries,
  localEntry: UpdateBatchEntries
) {
  const oldServerImageIds = oldServerEntry.receiptImageIds || [];
  const newServerImageIds = newServerEntry.receiptImageIds || [];
  const localImageIds = localEntry.receiptImageIds || [];

  const addedImageIds = newServerImageIds.filter(
    (id) => !oldServerImageIds.includes(id)
  );

  return [...localImageIds, ...addedImageIds];
}

function mergeLineItems(
  oldServerLineItems: LineItem[],
  newServerLineItems: LineItem[],
  localLineItems: LineItem[]
): LineItem[] {
  if (isEqual(localLineItems, newServerLineItems)) {
    return localLineItems;
  }

  const mergedItems: LineItem[] = [];
  const addToMergedItems = ({
    json: _,
    ...item
  }: LineItem & { json?: string }) => {
    mergedItems.push(item);
  };

  // Convert to map for efficient lookup
  const oldServerMap = new Map(
    oldServerLineItems.map((item) => [
      item.id,
      { ...item, json: stringifyItemForComparison(item) },
    ])
  );
  const newServerMap = new Map(
    newServerLineItems.map((item) => [
      item.id,
      { ...item, json: stringifyItemForComparison(item) },
    ])
  );
  const localMap = new Map(
    localLineItems.map((item) => [
      item.id ?? item._internalId,
      { ...item, json: stringifyItemForComparison(item) },
    ])
  );

  // Merge unchanged and updated items from server
  // eslint-disable-next-line complexity
  newServerLineItems.forEach(({ id }) => {
    if (id === undefined) {
      throw new Error('Server line items must have an id');
    }

    const oldRemoteItem = oldServerMap.get(id);
    const newRemoteItem = newServerMap.get(id)!;
    const localItem = localMap.get(id);

    const isNewlyCreatedRemoteItem = !oldRemoteItem;

    if (
      isNewlyCreatedRemoteItem ||
      (newRemoteItem.json !== oldRemoteItem.json &&
        localItem &&
        oldRemoteItem.json === localItem.json)
    ) {
      // If the item is new or updated on the server and locally unchanged
      addToMergedItems(newRemoteItem);
    } else if (!localItem) {
      // Item was locally deleted
    } else {
      // If the item is unchanged on the server, check for local changes
      if (
        localItem &&
        stringifyItemForComparison(localItem) !==
          stringifyItemForComparison(newRemoteItem)
      ) {
        // If locally modified, merge local changes
        addToMergedItems(localItem);
      } else {
        // Otherwise, use server version
        addToMergedItems(newRemoteItem);
      }
    }
  });

  // Check for deletions on the server
  oldServerLineItems.forEach(({ id }) => {
    if (id === undefined) {
      throw new Error('Server line items must have an id');
    }

    const oldRemoteItem = oldServerMap.get(id);
    const newRemoteItem = newServerMap.get(id)!;
    const localItem = localMap.get(id);

    if (!newRemoteItem) {
      // If a remote item does not exist remotely, it was removed
      // we only add it back, if it was locally changed
      if (localItem && oldRemoteItem?.json !== localItem.json) {
        addToMergedItems(localItem);
      }
    }
  });

  // Add new items from local that are not on the server
  localLineItems.forEach((item) => {
    const isNewItem = item.id === undefined;
    if (isNewItem) {
      addToMergedItems(item);
    }
  });

  return mergedItems;
}

function stringifyItemForComparison(obj: LineItem): string {
  // Exclude _internalId from comparison, as it will be different when re-fetching the remote data
  const { _internalId, ...rest } = obj;

  return JSON.stringify(sortObjectKeys(rest));
}

function sortObjectKeys<T extends object>(obj: T): T {
  return (Object.keys(obj) as Array<keyof object>).sort().reduce((acc, key) => {
    if (typeof obj[key] === 'object') {
      acc[key] = sortObjectKeys(obj[key]);
      return acc;
    }
    acc[key] = obj[key];
    return acc;
  }, {} as T);
}
